import firebase from 'firebase/app'
import 'firebase/firestore'
import axios from 'axios'
import format from 'date-fns/format'
import addDays from 'date-fns/add_days'
import _ from 'lodash'
import { adminBiasToCadence } from './intensity-mapping'
import { getCompanyTimezone, toCompanyDate } from './utils/companyTimezone'

/**
 * Cognitive Effort Score (CES) Calculator
 * 
 * Assigns a cognitive effort cost to each question based on its type and characteristics.
 * Used by the backend scheduler to allocate questions within a team's CES budget,
 * and by the admin console to display effort badges and budget previews.
 */
export function calculateEffortScore(question: any): number {
  if (!question) return 1.0

  const type = question.type || ''
  const customType = question.custom_type || ''
  const parent = question.parent || 'N'
  const mChoice = question.m_choice || {}
  const optionsCount = (mChoice.en && Array.isArray(mChoice.en)) ? mChoice.en.length : 0

  let score = 1.0

  // Open-ended questions require recall, composition, and reflection
  if (type === 'Text Area Field' || customType === 'open_ended') {
    score = 3.0
  }
  // Scale questions are simple anchored judgments
  else if (type === 'Scale' || customType === 'scale') {
    score = 1.0
  }
  // Multiple choice: depends on option count
  else if (type === 'Multiple Choice' || type === 'Multiple Choice(other)') {
    if (customType === 'yes_no' || optionsCount <= 2) {
      score = 0.5 // Yes/No: binary, near-zero deliberation
    } else if (optionsCount <= 3 || customType === 'multiple_choice') {
      score = 1.0 // 2-3 options: quick recognition
    } else {
      score = 1.5 // 4+ options: more deliberation
    }
  }

  // Follow-up questions add conditional depth
  if (parent === 'Y') {
    score += 0.5
  }

  return score
}

/**
 * Get a human-readable effort level label
 */
export function getEffortLevel(score: number): 'low' | 'medium' | 'high' {
  if (score <= 0.5) return 'low'
  if (score <= 1.5) return 'medium'
  return 'high'
}

/**
 * Get the tier classification for a question
 * - Tier 1 (essential): questions in the notblock_questions set
 * - Tier 2 (custom): company-specific questions (company !== 'main')
 * - Tier 3 (standard): Happily-curated questions from the master schedule
 */
export function getQuestionTier(question: any, notblockQuestions: any): 'essential' | 'custom' | 'standard' {
  if (notblockQuestions && notblockQuestions[question.key]) {
    return 'essential'
  }
  if (question.company && question.company !== 'main') {
    return 'custom'
  }
  return 'standard'
}

/**
 * Default CES budget for the Standard baseline
 */
const DEFAULT_CES_BUDGET = 10
const DEFAULT_ADMIN_BIAS = 0
const MIN_CES_BUDGET = 3
const MAX_CES_BUDGET = 18
const API_NODE = 'https://api-node.happily.ai'
//const API_NODE = 'http://localhost:80'

export default {
  namespaced: true,
  state: {
    // intensity_state for the company scope
    companyState: null as any,
    // intensity_state keyed by team_id
    teamStates: {} as { [key: string]: any },
    // Recent intensity_signals for the activity log
    signals: [] as any[],
    // Cached effort scores for questions: quest_id -> effort_score
    questionEffortScores: {} as { [key: string]: number },
    // Loading flags
    loading: false,
    loadingSignals: false,
    // Team DEBI data for the breakdown
    teamDebiData: {} as { [key: string]: any },
  },
  mutations: {
    setCompanyState(state: any, result: any) {
      state.companyState = result
    },
    setTeamStates(state: any, result: any) {
      state.teamStates = result
    },
    setSignals(state: any, result: any) {
      state.signals = result
    },
    setQuestionEffortScores(state: any, result: any) {
      state.questionEffortScores = result
    },
    setLoading(state: any, result: boolean) {
      state.loading = result
    },
    setLoadingSignals(state: any, result: boolean) {
      state.loadingSignals = result
    },
    setTeamDebiData(state: any, result: any) {
      state.teamDebiData = result
    },
  },
  actions: {
    /**
     * Load the intensity_state for the current company (and optionally teams)
     */
    async getIntensityState({ commit, rootState }: any) {
      commit('setLoading', true)
      const company = rootState.authen.user.company

      try {
        // Load company-level state
        const companySnapshot = await firebase.firestore()
          .collection('intensity_state')
          .where('company', '==', company)
          .where('scope', '==', 'company')
          .limit(1)
          .get()

        if (!companySnapshot.empty) {
          const doc = companySnapshot.docs[0]
          commit('setCompanyState', { id: doc.id, ...doc.data() })
        } else {
          // No saved state yet: expose a neutral in-memory baseline for older
          // getters, but leave companyState.id absent so the Questions panel
          // can show "Not set" and wait for an explicit admin choice.
          commit('setCompanyState', {
            company,
            scope: 'company',
            scope_id: company,
            ces_budget: DEFAULT_CES_BUDGET,
            admin_bias: DEFAULT_ADMIN_BIAS,
            maturity: 'not_initialized',
            last_adjustment: null,
            history: [],
          })
        }

        // Load team-level states
        const teamSnapshot = await firebase.firestore()
          .collection('intensity_state')
          .where('company', '==', company)
          .where('scope', '==', 'team')
          .get()

        const teamStates: { [key: string]: any } = {}
        teamSnapshot.forEach((doc: any) => {
          const data = doc.data()
          teamStates[data.scope_id] = { id: doc.id, ...data }
        })
        commit('setTeamStates', teamStates)
      } catch (error) {
        console.error('Error loading intensity state:', error)
      }

      commit('setLoading', false)
    },

    /**
     * Update the admin bias slider value
     */
    async setAdminBias({ commit, rootState, state, dispatch }: any, bias: number) {
      const company = rootState.authen.user.company
      const actorEmail = rootState.authen.user.email
      const clampedBias = Math.max(-2, Math.min(2, bias))
      const previousBias = state.companyState && typeof state.companyState.admin_bias === 'number'
        ? state.companyState.admin_bias
        : DEFAULT_ADMIN_BIAS
      const previousCadence = adminBiasToCadence(previousBias)
      const nextCadence = adminBiasToCadence(clampedBias)
      let intensityStateId = state.companyState && state.companyState.id
        ? state.companyState.id
        : company

      if (state.companyState && state.companyState.id) {
        // Update existing document
        await firebase.firestore()
          .collection('intensity_state')
          .doc(state.companyState.id)
          .update({
            admin_bias: clampedBias,
            updated_at: format(new Date(), 'YYYY-MM-DD HH:mm:ss'),
          })
      } else {
        // Create new document with default + bias
        const doc = await firebase.firestore()
          .collection('intensity_state')
          .add({
            company,
            scope: 'company',
            scope_id: company,
            ces_budget: DEFAULT_CES_BUDGET,
            admin_bias: clampedBias,
            maturity: 'not_initialized',
            observation_window_days: 14,
            last_adjustment: null,
            history: [],
            created_at: format(new Date(), 'YYYY-MM-DD HH:mm:ss'),
            updated_at: format(new Date(), 'YYYY-MM-DD HH:mm:ss'),
          })
        intensityStateId = doc.id
        commit('setCompanyState', {
          id: doc.id,
          company,
          scope: 'company',
          scope_id: company,
          ces_budget: DEFAULT_CES_BUDGET,
          admin_bias: clampedBias,
          maturity: 'not_initialized',
          last_adjustment: null,
          history: [],
        })
      }

      // Update local state
      if (state.companyState) {
        commit('setCompanyState', {
          ...state.companyState,
          admin_bias: clampedBias,
        })
      }

      await axios.post(`${API_NODE}/signup/sync-intensity`, {
        company,
        cadence: nextCadence,
        actorEmail,
      }, {
        headers: { 'Content-Type': 'application/json' },
      })

      if (previousCadence !== nextCadence) {
        await dispatch('authen/addLogs', {
          company,
          createdate: format(new Date(), 'YYYY-MM-DD HH:mm:ss'),
          id: intensityStateId,
          table: 'intensity_state',
          status: 'change_mode',
          user: actorEmail,
          from_mode: previousCadence,
          to_mode: nextCadence,
          detail: `Changed check-in intensity from ${previousCadence} to ${nextCadence}`,
        }, { root: true })
      }
    },

    /**
     * Load recent intensity signals for the activity log
     */
    async getIntensitySignals({ commit, rootState }: any) {
      commit('setLoadingSignals', true)
      const company = rootState.authen.user.company

      try {
        const snapshot = await firebase.firestore()
          .collection('intensity_signals')
          .where('company', '==', company)
          .orderBy('computed_at', 'desc')
          .limit(20)
          .get()

        const signals: any[] = []
        snapshot.forEach((doc: any) => {
          signals.push({ id: doc.id, ...doc.data() })
        })
        commit('setSignals', signals)
      } catch (error) {
        console.error('Error loading intensity signals:', error)
      }

      commit('setLoadingSignals', false)
    },

    /**
     * Compute effort scores for a set of questions and cache them
     */
    computeEffortScores({ commit }: any, questions: any[]) {
      const scores: { [key: string]: number } = {}
      for (const q of questions) {
        if (q.key) {
          scores[q.key] = calculateEffortScore(q)
        }
      }
      commit('setQuestionEffortScores', scores)
    },

    /**
     * Load team DEBI data for the team breakdown view
     */
    async getTeamDebiData({ commit, rootState }: any) {
      const company = rootState.authen.user.company
      const timezone = await getCompanyTimezone(company)
      const companyNow = toCompanyDate(new Date(), timezone)
      const yesterday = format(addDays(companyNow, -1), 'YYYY-MM-DD')

      try {
        const snapshot = await firebase.firestore()
          .collection('engagement_index_30day_rolling_team')
          .where('company', '==', company)
          .where('date', '==', yesterday)
          .get()

        const teamData: { [key: string]: any } = {}
        snapshot.forEach((doc: any) => {
          const data = doc.data()
          if (data.team) {
            teamData[data.team] = data
          }
        })
        commit('setTeamDebiData', teamData)
      } catch (error) {
        console.error('Error loading team DEBI data:', error)
      }
    },
  },
  getters: {
    /**
     * The effective CES budget for the company (budget + admin bias)
     */
    effectiveBudget(state: any): number {
      if (!state.companyState) return DEFAULT_CES_BUDGET
      const base = state.companyState.ces_budget || DEFAULT_CES_BUDGET
      const bias = state.companyState.admin_bias || 0
      return Math.max(MIN_CES_BUDGET, Math.min(MAX_CES_BUDGET, base + bias))
    },

    /**
     * The current admin bias value (-2 to +2)
     */
    adminBias(state: any): number {
      if (!state.companyState) return DEFAULT_ADMIN_BIAS
      return typeof state.companyState.admin_bias === 'number'
        ? state.companyState.admin_bias
        : DEFAULT_ADMIN_BIAS
    },

    /**
     * The maturity stage of the intensity system
     */
    maturity(state: any): string {
      return state.companyState ? (state.companyState.maturity || 'not_initialized') : 'not_initialized'
    },

    /**
     * Activity log entries from signals + state history
     */
    activityLog(state: any): any[] {
      const entries: any[] = []

      // Add entries from intensity_signals
      for (const signal of state.signals) {
        if (signal.adjustment && signal.adjustment.direction !== 'hold') {
          entries.push({
            date: signal.computed_at || signal.period,
            type: signal.adjustment.direction,
            description: signal.adjustment.reason || `Budget adjusted by ${signal.adjustment.ces_delta} CES`,
            budget: signal.adjustment.new_budget,
            debi: signal.debi ? signal.debi.current : null,
          })
        }
      }

      // Add entries from state history
      if (state.companyState && state.companyState.history) {
        for (const entry of state.companyState.history) {
          entries.push({
            date: entry.date,
            type: entry.direction,
            description: entry.reason,
            budget: entry.ces_budget,
          })
        }
      }

      // Sort by date descending and deduplicate
      return _.uniqBy(_.orderBy(entries, ['date'], ['desc']), 'date').slice(0, 10)
    },

    /**
     * Team breakdown: each team's DEBI score and effective budget
     */
    teamBreakdown(state: any): any[] {
      const teams: any[] = []
      for (const teamId in state.teamDebiData) {
        const debi = state.teamDebiData[teamId]
        const teamState = state.teamStates[teamId]
        const effectiveBudget = teamState
          ? Math.max(MIN_CES_BUDGET, Math.min(MAX_CES_BUDGET, (teamState.ces_budget || DEFAULT_CES_BUDGET) + (teamState.admin_bias || 0)))
          : (state.companyState ? Math.max(MIN_CES_BUDGET, Math.min(MAX_CES_BUDGET, (state.companyState.ces_budget || DEFAULT_CES_BUDGET) + (state.companyState.admin_bias || 0))) : DEFAULT_CES_BUDGET)

        teams.push({
          team_id: teamId,
          team_debi: debi.team_DEBI ? Number(debi.team_DEBI.toFixed(0)) : null,
          manager_debi: debi.manager_DEBI ? Number(debi.manager_DEBI.toFixed(0)) : null,
          ces_budget: effectiveBudget,
          has_override: !!teamState,
          team_members: debi.team_members || 0,
        })
      }
      return _.orderBy(teams, ['team_debi'], ['asc'])
    },

    /**
     * Summary health metrics for the company
     */
    healthSummary(state: any): any {
      const budget = state.companyState ? (state.companyState.ces_budget || DEFAULT_CES_BUDGET) : DEFAULT_CES_BUDGET
      const bias = state.companyState ? (state.companyState.admin_bias || 0) : 0
      const effective = Math.max(MIN_CES_BUDGET, Math.min(MAX_CES_BUDGET, budget + bias))

      // Estimate questions per day from budget
      // Average CES per question is ~1.2 (mix of types), so budget / 1.2 ≈ question count
      const estimatedQuestions = Math.round(effective / 1.2)
      // Open-ended at CES 3.0, so how many fit after essentials (~1.5 CES)
      const estimatedOpenEnded = Math.max(0, Math.floor((effective - 1.5) / 3.0))

      return {
        ces_budget: effective,
        estimated_questions: estimatedQuestions,
        estimated_open_ended: Math.min(estimatedOpenEnded, 3), // cap at limit.openend
        maturity: state.companyState ? (state.companyState.maturity || 'not_initialized') : 'not_initialized',
      }
    },
  }
}
